import React from "react";
import { useToast, VStack, FormControl, FormErrorMessage, HStack, Checkbox, CheckboxGroup, Stack, Switch, Text, Button } from "@chakra-ui/react";
import { StepperModal } from "../../../../_components/Modals";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../../homeowners/hire/themed-input";
import { organizationInfoSchema, contactInfoSchema, constructionDetailsSchema, OrganizationInfoFormData, ConstructionContactInfoFormData, ConstructionDetailsFormData } from "./schema";
import { ConstructionTrade, SimpleDuration } from "../../../../__generated__/graphql";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { gql, useMutation } from "@apollo/client";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { normalizeString } from "lib/utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userType: "business" | "homeowner";
}

const REQUEST_CONSTRUCTION_PROJECT = gql`
  mutation requestConstructionProject(
    $constructionProjectRequestDto: ConstructionProjectRequestDto!
  ) {
    requestConstructionProject(
      constructionProjectRequestDto: $constructionProjectRequestDto
    )
  }
`;

export const ConstructionProjectOutsourcingModal: React.FC<Props> = ({
  isOpen,
  onClose,
  userType,
}) => {
  const toast = useToast();
  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = userType === "business" ? 3 : 2;

  const [requestConstructionProject, { loading }] = useMutation(
    REQUEST_CONSTRUCTION_PROJECT
  );

  const orgForm = useForm<OrganizationInfoFormData>({
    resolver: zodResolver(organizationInfoSchema),
    defaultValues: { organization: "", roleInOrganization: "", industry: "" },
  });

  const contactForm = useForm<ConstructionContactInfoFormData>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: { firstName: "", lastName: "", email: "", phone: "" },
  });

  const detailsForm = useForm<ConstructionDetailsFormData>({
    resolver: zodResolver(constructionDetailsSchema),
    defaultValues: {
      projectLocation: "",
      intendedDuration: SimpleDuration.Lt_1Month,
      trades: [],
      otherTrade: "",
      estimatedArtisanCount: 1,
      willProvideMaterials: true,
      includeMaterialsInCost: undefined,
    },
  });

  const resetAll = () => {
    setCurrentStep(1);
    orgForm.reset();
    contactForm.reset();
    detailsForm.reset();
  };

  const handleSubmitAll = async () => {
    const organizationInfo =
      userType === "business" ? orgForm.getValues() : undefined;
    const contactInfo = contactForm.getValues();
    const details = detailsForm.getValues();

    try {
      await requestConstructionProject({
        variables: {
          constructionProjectRequestDto: {
            organizationInfo,
            contactInfo,
            projectLocation: details.projectLocation,
            intendedDuration: details.intendedDuration,
            trades: details.trades,
            otherTrade: details.otherTrade || undefined,
            estimatedArtisanCount: Number(details.estimatedArtisanCount),
            willProvideMaterials: details.willProvideMaterials,
            includeMaterialsInCost: details.willProvideMaterials
              ? undefined
              : !!details.includeMaterialsInCost,
          },
        },
      });

      toast({
        title: "Request Submitted Successfully",
        description:
          "Thank you for your request. Our customer success team will contact you shortly.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      resetAll();
      onClose();
    } catch (error) {
      toast({
        title: "Submission Failed",
        description:
          "Please try again or contact support if the issue persists.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const renderStep = () => {
    // Business: 1-org, 2-contact, 3-details
    // Homeowner: 1-contact, 2-details
    const isBusiness = userType === "business";
    if (isBusiness && currentStep === 1) {
      const {
        register,
        handleSubmit,
        formState: { errors },
      } = orgForm;
      return (
        <form onSubmit={handleSubmit(() => setCurrentStep(2))}>
          <VStack spacing={6} align="stretch">
            <FormControl isInvalid={!!errors.organization}>
              <ThemedFormLabel>Organization Name</ThemedFormLabel>
              <ThemedInput
                {...register("organization")}
                placeholder="Enter your organization name"
              />
              <FormErrorMessage>
                {errors.organization?.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.roleInOrganization}>
              <ThemedFormLabel>Your role in the organization</ThemedFormLabel>
              <ThemedInput
                {...register("roleInOrganization")}
                placeholder="e.g., CEO, Project Manager"
              />
              <FormErrorMessage>
                {errors.roleInOrganization?.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.industry}>
              <ThemedFormLabel>Industry (optional)</ThemedFormLabel>
              <ThemedInput
                {...register("industry")}
                placeholder="Enter your industry"
              />
              <FormErrorMessage>{errors.industry?.message}</FormErrorMessage>
            </FormControl>
            <Button
              type="submit"
              size="lg"
              w="full"
              colorScheme="flatteredFlamingo"
              rightIcon={<ArrowForwardIcon />}
              isLoading={loading}
              loadingText="Processing..."
              mt={4}
            >
              Next
            </Button>
          </VStack>
        </form>
      );
    }

    const {
      register: registerContact,
      handleSubmit: handleSubmitContact,
      formState: { errors: contactErrors },
    } = contactForm;
    if (
      (userType === "homeowner" && currentStep === 1) ||
      (userType === "business" && currentStep === 2)
    ) {
      return (
        <form
          onSubmit={handleSubmitContact(() => setCurrentStep(currentStep + 1))}
        >
          <VStack spacing={6} align="stretch">
            <HStack spacing={4}>
              <FormControl isInvalid={!!contactErrors.firstName}>
                <ThemedFormLabel>First Name</ThemedFormLabel>
                <ThemedInput
                  {...registerContact("firstName")}
                  placeholder="Enter your first name"
                />
                <FormErrorMessage>
                  {contactErrors.firstName?.message}
                </FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!contactErrors.lastName}>
                <ThemedFormLabel>Last Name</ThemedFormLabel>
                <ThemedInput
                  {...registerContact("lastName")}
                  placeholder="Enter your last name"
                />
                <FormErrorMessage>
                  {contactErrors.lastName?.message}
                </FormErrorMessage>
              </FormControl>
            </HStack>
            <FormControl isInvalid={!!contactErrors.email}>
              <ThemedFormLabel>Email Address</ThemedFormLabel>
              <ThemedInput
                type="email"
                {...registerContact("email")}
                placeholder="Enter your email"
              />
              <FormErrorMessage>
                {contactErrors.email?.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!contactErrors.phone}>
              <ThemedFormLabel>Phone Number</ThemedFormLabel>
              <ThemedInput
                type="tel"
                {...registerContact("phone")}
                placeholder="Enter your phone number"
              />
              <FormErrorMessage>
                {contactErrors.phone?.message}
              </FormErrorMessage>
            </FormControl>
            <HStack spacing={4} mt={4}>
              {currentStep > 1 && userType === "business" && (
                <Button
                  type="button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  leftIcon={<ArrowBackIcon />}
                  size="lg"
                  flex="1"
                  variant="outline"
                  isDisabled={loading}
                >
                  Previous
                </Button>
              )}
              <Button
                type="submit"
                size="lg"
                flex="1"
                colorScheme="flatteredFlamingo"
                rightIcon={<ArrowForwardIcon />}
                isLoading={loading}
                loadingText="Processing..."
              >
                Next
              </Button>
            </HStack>
          </VStack>
        </form>
      );
    }

    const {
      register: registerDetails,
      handleSubmit: handleSubmitDetails,
      watch,
      setValue,
      formState: { errors: detailErrors },
    } = detailsForm;

    const trades = watch("trades") as unknown as ConstructionTrade[];
    const willProvideMaterials = watch("willProvideMaterials");

    const toggleTrade = (value: string[]) => {
      setValue("trades", value as ConstructionTrade[], {
        shouldValidate: true,
      });
    };

    return (
      <form onSubmit={handleSubmitDetails(() => handleSubmitAll())}>
        <VStack spacing={6} align="stretch">
          <FormControl isInvalid={!!detailErrors.projectLocation}>
            <ThemedFormLabel>Project Location</ThemedFormLabel>
            <ThemedInput
              {...registerDetails("projectLocation")}
              placeholder="Enter project location"
            />
            <FormErrorMessage>
              {detailErrors.projectLocation?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!detailErrors.intendedDuration}>
            <ThemedFormLabel>Intended Duration of Project</ThemedFormLabel>
            <select
              {...registerDetails("intendedDuration")}
              className="min-w-full chakra-input css-0"
              style={{ padding: "10px", border: "1px solid #E2E8F0" }}
            >
              <option value={SimpleDuration.Lt_1Month}>
                Less than 1 month
              </option>
              <option value={SimpleDuration.OneToThreeMonths}>
                1-3 months
              </option>
              <option value={SimpleDuration.ThreeToSixMonths}>
                3-6 months
              </option>
              <option value={SimpleDuration.Gt_6Months}>Over 6 months</option>
            </select>
            <FormErrorMessage>
              {detailErrors.intendedDuration?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!detailErrors.trades}>
            <ThemedFormLabel>Which trades do you require?</ThemedFormLabel>
            <CheckboxGroup
              value={trades as any}
              onChange={(v) => toggleTrade(v as string[])}
            >
              <Stack direction="column">
                {Object.values(ConstructionTrade)
                  .filter((trade) => trade !== ConstructionTrade.Other)
                  .map((trade) => (
                    <Checkbox key={trade} value={trade}>
                      {normalizeString(trade)}
                    </Checkbox>
                  ))}
                <Checkbox value={ConstructionTrade.Other}>
                  {normalizeString(ConstructionTrade.Other)}
                </Checkbox>
              </Stack>
            </CheckboxGroup>
            <FormErrorMessage>
              {(detailErrors as any).trades?.message}
            </FormErrorMessage>
          </FormControl>

          {trades?.includes(ConstructionTrade.Other) && (
            <FormControl isInvalid={!!detailErrors.otherTrade}>
              <ThemedFormLabel>Other trade (please specify)</ThemedFormLabel>
              <ThemedInput
                {...registerDetails("otherTrade")}
                placeholder="e.g., Landscaping"
              />
              <FormErrorMessage>
                {detailErrors.otherTrade?.message}
              </FormErrorMessage>
            </FormControl>
          )}

          <FormControl isInvalid={!!detailErrors.estimatedArtisanCount}>
            <ThemedFormLabel>How many artisans do you require?</ThemedFormLabel>
            <ThemedInput
              type="number"
              {...registerDetails("estimatedArtisanCount", {
                valueAsNumber: true,
              })}
              placeholder="e.g., 5"
            />
            <FormErrorMessage>
              {detailErrors.estimatedArtisanCount?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl>
            <ThemedFormLabel>
              Will you provide materials for this project?
            </ThemedFormLabel>
            <HStack>
              <Text>No</Text>
              <Switch
                isChecked={willProvideMaterials}
                onChange={(e) =>
                  setValue("willProvideMaterials", e.target.checked, {
                    shouldValidate: true,
                  })
                }
              />
              <Text>Yes</Text>
            </HStack>
          </FormControl>

          {!willProvideMaterials && (
            <FormControl isInvalid={!!detailErrors.includeMaterialsInCost}>
              <ThemedFormLabel>
                Would you like us to include materials in the service cost?
              </ThemedFormLabel>
              <HStack>
                <Text>No</Text>
                <Switch
                  isChecked={!!watch("includeMaterialsInCost")}
                  onChange={(e) =>
                    setValue("includeMaterialsInCost", e.target.checked, {
                      shouldValidate: true,
                    })
                  }
                />
                <Text>Yes</Text>
              </HStack>
              <FormErrorMessage>
                {detailErrors.includeMaterialsInCost?.message}
              </FormErrorMessage>
            </FormControl>
          )}

          <HStack spacing={4} mt={4}>
            <Button
              type="button"
              onClick={() => setCurrentStep(currentStep - 1)}
              leftIcon={<ArrowBackIcon />}
              size="lg"
              flex="1"
              variant="outline"
              isDisabled={loading}
            >
              Previous
            </Button>
            <Button
              type="submit"
              size="lg"
              flex="1"
              colorScheme="flatteredFlamingo"
              rightIcon={<ArrowForwardIcon />}
              isLoading={loading}
              loadingText="Submitting..."
            >
              Submit Request
            </Button>
          </HStack>
        </VStack>
      </form>
    );
  };

  const titleForStep = () => {
    if (userType === "business") {
      switch (currentStep) {
        case 1:
          return "Tell us about your organization";
        case 2:
          return "Tell us about yourself";
        case 3:
          return "Project details";
        default:
          return "Construction Project";
      }
    }
    switch (currentStep) {
      case 1:
        return "Tell us about yourself";
      case 2:
        return "Project details";
      default:
        return "Construction Project";
    }
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const onNext = async () => {
    if (userType === "business") {
      if (currentStep === 1) {
        const valid = await orgForm.trigger();
        if (valid) setCurrentStep(2);
        return;
      }
      if (currentStep === 2) {
        const valid = await contactForm.trigger();
        if (valid) setCurrentStep(3);
        return;
      }
      if (currentStep === 3) {
        const valid = await detailsForm.trigger();
        if (valid) await handleSubmitAll();
        return;
      }
    } else {
      if (currentStep === 1) {
        const valid = await contactForm.trigger();
        if (valid) setCurrentStep(2);
        return;
      }
      if (currentStep === 2) {
        const valid = await detailsForm.trigger();
        if (valid) await handleSubmitAll();
        return;
      }
    }
  };

  const onPrev = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  return (
    <StepperModal
      isOpen={isOpen}
      onClose={handleClose}
      title={titleForStep()}
      onSubmit={onNext}
      currentStep={currentStep}
      totalSteps={totalSteps}
      isLoading={loading}
      onReset={resetAll}
      onPrevious={onPrev}
    >
      {renderStep()}
    </StepperModal>
  );
};

